/**
 * Entwürfe — der geführte Ablauf nach der Quelle.
 *
 * 🔴 Umgebaut am 15.09.2026 nach Matzes Rückmeldung: „Ich weiß nicht, was aktualisiert
 * wird oder was neu angelegt wird. Mir fehlt ein klarer roter Faden." Vorher standen
 * Neuanlage und Aktualisieren als fünf Blöcke untereinander auf einer Seite, mit zwei
 * verschiedenen Tippbestätigungen — ohne dass je gewählt worden wäre, was passieren soll.
 *
 * Jetzt gilt: **Ein Entwurf hat eine Betriebsart, und nur ihr Pfad ist sichtbar.**
 *
 *   Neue Artikel anlegen           Bestand erweitern / aktualisieren
 *   ① Auswahl                      ① Auswahl (die vorhandenen Artikel)
 *   ② Eigenschaften und Tags       ② Felder
 *   ③ Probelauf                    ③ Vorschau
 *   ④ Anlegen                      ④ Schreiben
 *                                  ⑤ Bilder (optional)
 *
 * Jeder Schritt hat eine Nummer, einen Titel und eine Zeile Zustand. Was nicht dran ist,
 * ist zugeklappt oder gesperrt — aber sichtbar, damit klar ist, was noch kommt.
 *
 * 🔴 Es gibt keine Plenty-Sandbox: auch Dev-Instanzen schreiben in den echten Shop
 * (E31). Der Probelauf schreibt nichts; erst Schritt ④ legt Artikel an.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import AktualisierenPanel from '../components/AktualisierenPanel';
import ErweiterungPanel from '../components/ErweiterungPanel';
import PruefAnsicht from '../components/PruefAnsicht';
import BetriebsartWahl, { betriebsartName, type Betriebsart } from '../components/BetriebsartWahl';
import Info from '../components/Info';
import ProbelaufPlan from '../components/ProbelaufPlan';
import RegelnPanel from '../components/RegelnPanel';
import TeamlinePanel from '../components/TeamlinePanel';
import LieferzeitFeld from '../components/LieferzeitFeld';
import FehlendeBilder from '../components/FehlendeBilder';
import Schritt from '../components/Schritt';
import SchreiblaufPanel from '../components/SchreiblaufPanel';
import Vorsortierung, { filtere, hatBilder, type GruppenSchluessel } from '../components/Vorsortierung';
import { existenzBefund } from '../lib/existenz';
import { zahl, zeitpunkt } from '../lib/format';
import type {
  EntwurfDetail, EntwurfKurz, ProbelaufAntwort, SchreibRegeln,
} from '../lib/typen';

export default function EntwuerfePage({ oeffneId = null, aufGeoeffnet }: {
  /** Diesen Entwurf gleich öffnen — so landet „Entwurf erstellen" direkt im neuen. */
  oeffneId?: number | null;
  aufGeoeffnet?: () => void;
} = {}) {
  const [entwuerfe, setEntwuerfe] = useState<EntwurfKurz[]>([]);
  const [offen, setOffen] = useState<EntwurfDetail | null>(null);
  const [probelauf, setProbelauf] = useState<ProbelaufAntwort | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Die FTP-Ordner braucht nur „Bilder nachliefern" — ein Fehler dabei darf die
  // Entwurfsliste nicht kippen.
  /** Welche Gruppe die Liste zeigt und worauf die Sammelauswahl wirkt. */
  const [gruppe, setGruppe] = useState<GruppenSchluessel>('alle');
  /** Eine laufende Sammelauswahl — sie kann 241 Artikel betreffen und dauert. */
  const [waehltAus, setWaehltAus] = useState(false);
  const [stelltUm, setStelltUm] = useState(false);
  /** Rückmeldung nach einer Kategorie-Zuweisung — verschwindet beim nächsten Öffnen. */
  const [hinweis, setHinweis] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const antwort = await api.get<{ data: EntwurfKurz[] }>('/programmes/artikelimport/entwuerfe');
      setEntwuerfe(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Entwürfe nicht ladbar.');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  const oeffnen = useCallback(async (id: number) => {
    setFehler(null);
    setProbelauf(null);
    setHinweis(null);
    try {
      const antwort = await api.get<{ data: EntwurfDetail }>(`/programmes/artikelimport/entwuerfe/${id}`);
      setOffen(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Entwurf nicht ladbar.');
    }
  }, []);

  // Aus „Entwurf erstellen" gekommen: den neuen Entwurf gleich öffnen, einmal.
  useEffect(() => {
    if (oeffneId == null) return;
    void oeffnen(oeffneId).finally(() => aufGeoeffnet?.());
  }, [oeffneId, oeffnen, aufGeoeffnet]);

  const auswahlSetzen = useCallback(async (
    id: number, artikel: string[], ausgewaehlt: boolean,
  ) => {
    if (artikel.length === 0) return;
    setWaehltAus(true);
    try {
      await api.patch(`/programmes/artikelimport/entwuerfe/${id}/auswahl`, { artikel, ausgewaehlt });
      await oeffnen(id);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Auswahl nicht speicherbar.');
    } finally {
      setWaehltAus(false);
    }
  }, [oeffnen]);

  /**
   * Betriebsart umstellen — wer sich in der Quelle vertan hat, muss nicht neu einlesen.
   * Das Backend belegt die Auswahl passend neu vor (alles Passende an, alles andere ab).
   */
  const betriebsartUmstellen = useCallback(async (id: number, betriebsart: Betriebsart) => {
    setStelltUm(true);
    setFehler(null);
    setProbelauf(null);
    try {
      await api.patch(`/programmes/artikelimport/entwuerfe/${id}/betriebsart`, { betriebsart });
      setGruppe('alle');
      await oeffnen(id);
      await laden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Umstellen fehlgeschlagen.');
    } finally {
      setStelltUm(false);
    }
  }, [oeffnen, laden]);

  const probelaufStarten = useCallback(async (id: number) => {
    setLaeuft(true);
    setFehler(null);
    setProbelauf(null);
    try {
      const antwort = await api.post<{ data: ProbelaufAntwort }>(
        `/programmes/artikelimport/entwuerfe/${id}/probelauf`, {},
      );
      setProbelauf(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Probelauf fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  }, []);

  const verwerfen = useCallback(async (id: number) => {
    await api.delete(`/programmes/artikelimport/entwuerfe/${id}`);
    if (offen?.entwurf.id === id) setOffen(null);
    await laden();
  }, [laden, offen]);

  // ── Ableitungen für den geöffneten Entwurf ──────────────────────────────
  const betriebsart: Betriebsart = offen?.entwurf.betriebsart === 'felder' ? 'felder' : 'neuanlage';
  const neuanlage = betriebsart === 'neuanlage';
  const sortierbar = (offen?.artikel ?? []).map(a => ({
    ...a,
    schluessel: a.artikelSchluessel,
    // 🔴 NICHT `a.status` — das ist der Zeilenzustand (`bereit`/`uebersprungen`), die
    // Vorsortierung braucht den Existenzbefund. Genau diese Verwechslung liess die
    // Leiste „Anlegen 0 · Unklar 88" anzeigen (22.09.2026).
    existenz: existenzBefund(a),
    ampelKategorie: a.ampelKategorie,
    ampelBild: a.ampelBild,
  }));
  const gefiltert = filtere(sortierbar, gruppe);
  // Was in dieser Betriebsart überhaupt anwählbar ist — die Regel steht über dem Klick.
  // Neuanlage: nur Neues MIT Bildern (Matze 28.09.2026) — das Backend prüft dasselbe.
  const anwaehlbar = (a: { status: string; ampelBild: string | null }) => (neuanlage
    ? a.status !== 'uebersprungen' && hatBilder(a.ampelBild)
    : a.status === 'uebersprungen');

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Entwürfe</h1>
        <Button variant="outline" onClick={() => void laden()} disabled={laedt}>
          <RefreshCw className={`mr-2 h-4 w-4 ${laedt ? 'animate-spin' : ''}`} />
          {laedt ? 'Wird geladen …' : 'Neu laden'}
        </Button>
      </header>

      {fehler && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {fehler}
        </div>
      )}

      {hinweis && (
        <div className="rounded border border-[#1A1A1A] bg-[#E9FF57]/30 p-3 text-sm text-[#1A1A1A]">
          {hinweis}
        </div>
      )}

      {/* ── Liste ─────────────────────────────────────────────────────────── */}
      <section className="space-y-2">
        {entwuerfe.length === 0 && !laedt && (
          <p className="text-sm text-[#1A1A1A]/60">Noch kein Entwurf — unter „Quelle" anlegen.</p>
        )}
        {entwuerfe.map(e => (
          <div
            key={e.id}
            className={`flex flex-wrap items-center gap-3 rounded border p-3 text-sm ${
              offen?.entwurf.id === e.id ? 'border-[#1A1A1A] bg-[#E9FF57]/20' : 'border-[#DAD9D9]'
            }`}
          >
            <button
              type="button"
              onClick={() => void oeffnen(e.id)}
              className="min-w-0 flex-1 text-left"
            >
              <span className="font-medium text-[#1A1A1A]">{e.name}</span>
              <span className="ml-2 text-[#1A1A1A]/60">
                {zahl(e.artikelGesamt)} Artikel · {zahl(e.variantenGesamt)} Varianten
                {e.gerechnetAm && ` · ${zeitpunkt(e.gerechnetAm)}`}
              </span>
            </button>
            <span className={`rounded px-2 py-0.5 text-xs ${
              e.betriebsart === 'felder' ? 'bg-amber-50 text-amber-900' : 'bg-green-50 text-green-900'
            }`}>
              {betriebsartName(e.betriebsart)}
            </span>
            {e.benutzerName && (
              <span className="text-xs text-[#1A1A1A]/50">
                {e.eigener ? 'von dir' : e.benutzerName}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => void verwerfen(e.id)} title="Entwurf verwerfen">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </section>

      {/* ── Ein Entwurf: Kopf mit Betriebsart ─────────────────────────────── */}
      {offen && (
        <section className="space-y-3 rounded border-2 border-[#1A1A1A] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#1A1A1A]">{offen.entwurf.name}</h2>
              <p className="text-sm text-[#1A1A1A]/70">
                Quelle: {offen.entwurf.quelle?.dateiname ?? '–'} · {zahl(offen.artikel.length)} Artikel
              </p>
            </div>
            <div className="space-y-1 text-right">
              <p className="flex items-center justify-end gap-1 text-xs uppercase tracking-wide text-[#1A1A1A]/60">
                Betriebsart
                <Info>
                  {neuanlage
                    ? <p>Legt <strong>neue Artikel</strong> an. Vorhandene werden übersprungen.</p>
                    : <p>Aktualisiert <strong>bestehende Artikel</strong>. Neue bleiben außen vor.</p>}
                </Info>
              </p>
              <BetriebsartWahl
                kompakt
                wert={betriebsart}
                aufWahl={b => { if (b !== betriebsart && !stelltUm) void betriebsartUmstellen(offen.entwurf.id, b); }}
              />
              {stelltUm && <p className="text-xs text-[#1A1A1A]/60">wird umgestellt …</p>}
            </div>
          </div>
        </section>
      )}

      {/* ── Schritt 1: Auswahl (beide Pfade) ───────────────────────────────── */}
      {offen && (
        <Schritt
          nummer={1}
          titel="Artikel prüfen"
          zustand={`${zahl(offen.ausgewaehlt)} von ${zahl(offen.artikel.length)} ausgewählt`}
          erledigt={offen.ausgewaehlt > 0}
          // Die Prüfansicht steckt hier — sie bleibt offen, auch wenn schon ausgewählt ist.
          offen
        >
          <div className="space-y-3">
            <Vorsortierung
              artikel={sortierbar}
              aktiv={gruppe}
              aufGruppe={setGruppe}
              beschaeftigt={waehltAus}
              aufAuswahl={(schluessel, ausgewaehlt) => void auswahlSetzen(
                offen.entwurf.id,
                // Die Regel steht über dem Klick: nur, was in dieser Betriebsart Ziel
                // sein kann. Abwählen darf immer.
                ausgewaehlt
                  ? schluessel.filter(sch => {
                    const a = offen.artikel.find(x => x.artikelSchluessel === sch);
                    return a ? anwaehlbar(a) : false;
                  })
                  : schluessel,
                ausgewaehlt,
              )}
            />

            {neuanlage && <FehlendeBilder entwurfId={offen.entwurf.id} entwurfName={offen.entwurf.name} />}

            <PruefAnsicht
              entwurfId={offen.entwurf.id}
              artikel={gefiltert}
              neuanlage={neuanlage}
              anwaehlbar={anwaehlbar}
              waehltAus={waehltAus}
              aufAuswahl={(schluessel, ausgewaehlt) => void auswahlSetzen(offen.entwurf.id, [schluessel], ausgewaehlt)}
              auswahlKennung={offen.artikel.filter(a => a.ausgewaehlt).map(a => a.artikelSchluessel).join('|')}
              aufListeNeu={() => void oeffnen(offen.entwurf.id)}
            />
          </div>
        </Schritt>
      )}

      {/* ══ Pfad NEUANLAGE — drei Schritte (Phase 5, 28.09.2026) ══════════════ */}
      {offen && neuanlage && (
        <>
          <Schritt
            nummer={2}
            titel="Für alle einstellen"
            zustand={`${zahl(offen.entwurf.regeln.eigenschaften.length)} Eigenschaften · ${zahl(offen.entwurf.regeln.tags.length)} Tags · Verfügbar bis`}
            erledigt
          >
            <div className="space-y-6">
              <RegelnPanel
                entwurfId={offen.entwurf.id}
                regeln={offen.entwurf.regeln}
                kategoriewerte={offen.entwurf.kategoriewerte}
                aufRegelnGeaendert={(regeln: SchreibRegeln) => {
                  setProbelauf(null);
                  setOffen(v => (v ? { ...v, entwurf: { ...v.entwurf, regeln } } : v));
                }}
              />
              <div className="border-t border-[#DAD9D9] pt-4">
                <TeamlinePanel entwurfId={offen.entwurf.id} />
                <LieferzeitFeld entwurfId={offen.entwurf.id} />
              </div>
            </div>
          </Schritt>

          <Schritt
            nummer={3}
            titel="In Plenty anlegen"
            ton="gefahr"
            zustand={laeuft
              ? 'Probelauf rechnet …'
              : probelauf
                ? `${zahl(probelauf.plan.summen.anlegen)} Artikel · ${zahl(probelauf.plan.summen.variantenAktiv)} Varianten`
                : `${zahl(offen.ausgewaehlt)} ausgewählt`}
            gesperrt={offen.ausgewaehlt === 0}
            gesperrtGrund="Erst Artikel auswählen."
            offen={false}
            aufOeffnen={() => { if (!probelauf && !laeuft) void probelaufStarten(offen.entwurf.id); }}
            hilfe={(
              <>
                <p>Vorher rechnet ein Probelauf alles durch und schreibt nichts.</p>
                <p>
                  Die Artikel entstehen inaktiv und gehen erst über „Aktivieren" live. Ein
                  Abbruch hält nach dem aktuellen Artikel an; was angelegt ist, bleibt.
                </p>
                <p>Ein angelegter Artikel hakt sich im Entwurf selbst ab und wird nicht noch einmal angelegt.</p>
              </>
            )}
          >
            <div className="space-y-4">
              {laeuft && (
                <p className="flex items-center gap-2 text-sm text-[#6B6A66]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Probelauf rechnet jeden Artikel durch — schreibt nichts …
                </p>
              )}
              {!laeuft && !probelauf && (
                <button
                  type="button"
                  onClick={() => void probelaufStarten(offen.entwurf.id)}
                  className="rounded bg-[#E9FF57] px-3 py-1.5 text-sm font-semibold text-[#1A1A1A] hover:bg-[#d4ea4d]"
                >
                  Probelauf rechnen
                </button>
              )}
              {probelauf && (
                <>
                  <ProbelaufPlan
                    ergebnis={probelauf}
                    entwurfId={offen.entwurf.id}
                    aufKategorieGesetzt={meldung => {
                      // Nach der Zuweisung beides neu holen: die Liste zeigt die Ampel,
                      // der Probelauf die Kategoriezahl. Sonst steht die alte Aussage da.
                      setHinweis(meldung);
                      void oeffnen(offen.entwurf.id).then(() => probelaufStarten(offen.entwurf.id));
                    }}
                  />
                  <button type="button" onClick={() => void probelaufStarten(offen.entwurf.id)} className="text-xs underline">
                    neu rechnen
                  </button>
                  <SchreiblaufPanel
                    entwurfId={offen.entwurf.id}
                    ausgewaehlt={offen.ausgewaehlt}
                    artikel={offen.artikel}
                    berichtEmpfaenger={offen.entwurf.berichtEmpfaenger}
                  />
                </>
              )}
            </div>
          </Schritt>
        </>
      )}

      {/* ══ Pfad AKTUALISIEREN ══════════════════════════════════════════════ */}
      {offen && !neuanlage && (
        <>
          {/* Neue Farben/Größen an bestehende Artikel (16.10.2026) — vor den Feldern. */}
          <ErweiterungPanel entwurfId={offen.entwurf.id} schritt={2} />
          <AktualisierenPanel entwurfId={offen.entwurf.id} schrittAb={3} />
        </>
      )}
    </div>
  );
}
